import { useState, useEffect, createContext, useContext, useRef, useCallback } from 'react';
import { User, Session, AuthError } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isSessionValid: boolean;
  signIn: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signUp: (email: string, password: string, metadata?: { first_name: string; last_name: string; display_name: string }) => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<{ error: AuthError | null }>;
  refreshSession: () => Promise<{ error: AuthError | null }>;
  checkAuthStatus: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Move validateSession outside the hook since it's a pure function
const validateSession = (session: Session | null): boolean => {
  if (!session) return false;
  
  const now = Date.now() / 1000;
  const expiresAt = session.expires_at;
  
  // Check if session expires within the next 5 minutes
  return expiresAt ? expiresAt > (now + 300) : false;
};

export const useAuthProvider = () => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isSessionValid, setIsSessionValid] = useState(false);

  // Refs to avoid stale closures in intervals
  const userRef = useRef<User | null>(null);
  const sessionRef = useRef<Session | null>(null);

  useEffect(() => {
    userRef.current = user;
    sessionRef.current = session;
  }, [user, session]);

  // Refresh the current session - memoized
  const refreshSession = useCallback(async () => {
    try {
      const { data, error } = await supabase.auth.refreshSession();
      if (error) {
        console.error('Session refresh failed:', error);
        return { error };
      }
      return { error: null };
    } catch (error) {
      console.error('Session refresh error:', error);
      return { error: error as AuthError };
    }
  }, []);

  // Check current auth status - memoized
  const checkAuthStatus = useCallback(async (): Promise<boolean> => {
    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      if (error) {
        console.error('Auth status check failed:', error);
        return false;
      }
      
      const valid = validateSession(session);
      setIsSessionValid(valid);
      return valid;
    } catch (error) {
      console.error('Auth status check error:', error);
      setIsSessionValid(false);
      return false;
    }
  }, []);

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log('Auth state change:', event, session ? 'session present' : 'no session');
        
        setSession(session);
        setUser(session?.user ?? null);
        setIsSessionValid(validateSession(session));
        setLoading(false);
        
        // Handle specific auth events
        if (event === 'TOKEN_REFRESHED') {
          console.log('Token refreshed successfully');
        } else if (event === 'SIGNED_OUT') {
          console.log('User signed out');
          setIsSessionValid(false);
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      console.log('Initial session check:', session ? 'session found' : 'no session');
      setSession(session);
      setUser(session?.user ?? null);
      setIsSessionValid(validateSession(session));
      setLoading(false);
    });

    // Set up periodic session validation (every 5 minutes to reduce load)
    const sessionCheckInterval = setInterval(async () => {
      const currentSession = sessionRef.current;
      const currentUser = userRef.current;
      if (currentSession && currentUser && validateSession(currentSession)) {
        const valid = await checkAuthStatus();
        if (!valid) {
          console.log('Session invalid or near expiry, attempting refresh...');
          // Only refresh if we haven't refreshed recently to prevent loops
          const lastRefresh = localStorage.getItem('lastSessionRefresh');
          const now = Date.now();
          if (!lastRefresh || (now - parseInt(lastRefresh)) > 30000) { // 30 second cooldown
            localStorage.setItem('lastSessionRefresh', now.toString());
            const result = await refreshSession();
            if (result.error) {
              console.error('Session refresh failed, user may need to re-authenticate');
            }
          }
        }
      }
    }, 5 * 60 * 1000); // 5 minutes

    return () => {
      subscription.unsubscribe();
      clearInterval(sessionCheckInterval);
    };
  }, [checkAuthStatus, refreshSession]);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error };
  };

  const signUp = async (email: string, password: string, metadata?: { first_name: string; last_name: string; display_name: string }) => {
    const redirectUrl = `${window.location.origin}/`;
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: metadata || {}
      }
    });
    return { error };
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    return { error };
  };

  return {
    user,
    session,
    loading,
    isSessionValid,
    signIn,
    signUp,
    signOut,
    refreshSession,
    checkAuthStatus,
  };
};

export { AuthContext };